import { useState, useEffect } from 'react';
import CustomTextbox from './common/CustomTextbox';
import CustomButton from './common/CustomButton';
import { en } from '../assets/labels-en';
import './PropertyInfoForm.css';
import { useNavigate } from 'react-router-dom';
import { authenticate } from '../services/escheatmentservice';
import { useTenant } from '../context/TenantContext';
import CustomModal from './common/CustomModal';
import { useRecaptchaEnterprise } from '../hooks/useRecaptchaEnterprise';
import LoaderComponent from './common/LoaderComponent';

const PropertyInfoForm = () => {
  const [formData, setFormData] = useState({
    referenceNumber: '',
    lastName: '',
    postalCode: '',
  });
  const [errors, setErrors] = useState({
    referenceNumber: false,
    lastName: false,
    postalCode: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { tenantSlug } = useTenant();
  const { isReady, executeRecaptcha } = useRecaptchaEnterprise();

  const recaptchaEnabled = import.meta.env.VITE_RECAPTCHA_ENABLED === 'Y';

  useEffect(() => {
    if (recaptchaEnabled && !isReady) {
      console.log('Waiting for reCAPTCHA Enterprise to be ready...');
    } else if (recaptchaEnabled && isReady) {
      console.log('reCAPTCHA Enterprise is ready');
    }
  }, [isReady, recaptchaEnabled]);

  /**
   * Execute reCAPTCHA Enterprise and return token
   * @param {string} action - The action name for reCAPTCHA
   * @returns {Promise<string>} - The reCAPTCHA token or empty string
   */
  const executerecaptcha = async (action) => {
    if (!recaptchaEnabled) {
      console.log('reCAPTCHA Enterprise is disabled');
      return '';
    }

    if (!executeRecaptcha) {
      console.warn('executeRecaptcha not available yet');
      return '';
    }

    try {
      const token = await executeRecaptcha(action);
      return token || '';
    } catch (error) {
      console.error('Error executing reCAPTCHA Enterprise:', error);
      return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      referenceNumber: false,
      lastName: false,
      postalCode: false,
    };

    // Validate reference number: exactly 16 digits, only numbers, starts with '0401'
    const referenceNumberRegex = /^0401\d{12}$/;
    if (
      !formData.referenceNumber ||
      !referenceNumberRegex.test(formData.referenceNumber)
    ) {
      newErrors.referenceNumber = true;
    }

    // Validate last name: not empty
    if (!formData.lastName || !formData.lastName.trim()) {
      newErrors.lastName = true;
    }

    // Validate postal code: not empty
    if (!formData.postalCode || !formData.postalCode.trim()) {
      newErrors.postalCode = true;
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before API call
    const isValid = validateForm();

    if (!isValid) {
      return;
    }
    setIsSubmitting(true);
    try {
      // Generate reCAPTCHA token
      const recaptchaToken = await executerecaptcha('verify');

      const payload = {
        ddaNumber: formData.referenceNumber,
        lastName: formData.lastName,
        postalCode: formData.postalCode,
      };

      const response = await authenticate(payload, tenantSlug, recaptchaToken);
      if (
        response?.status === 200 &&
        response.data?.verificationStatusCode === 'SUCCESS'
      ) {
        navigate(`/${tenantSlug}/confirm-address`, {
          replace: true,
          state: { 
            from: 'property-info-form', 
            data: response?.data,
            jwtToken: response?.jwtToken, // Pass JWT token to next page
          },
        });
      } else {
        setErrorMessage(
          'An unexpected error occurred. Please try again later.'
        );
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error?.message);
      const status = error?.response?.status;
      const errorFromResponse = error?.response?.data?.message;
      let errorMsg = 'An unexpected error occurred. Please try again later.';
      if (status === 400) {
        errorMsg =
          errorFromResponse ||
          'Invalid input. Please check your data and try again.';
      } else if (status === 401) {
        errorMsg =
          errorFromResponse ||
          "Your information doesn't match our records. Please try again.";
      } else if (errorFromResponse) {
        errorMsg = errorFromResponse;
      }
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <LoaderComponent showloader={isSubmitting} />
      <div className="property-info-form-container">
        <div className="property-info-form-content">
          <h2 className="form-title">{en.property_info_form_title}</h2>
          <form onSubmit={handleSubmit} className="property-form" noValidate>
            <CustomTextbox
              label={en.reference_number_label}
              placeholder={en.reference_number_label + '*'}
              name="referenceNumber"
              value={formData.referenceNumber}
              onChange={handleChange}
              required={true}
              showError={errors.referenceNumber}
              errorMessage={en.reference_number_error}
              maxLength={16}
            />
            <CustomTextbox
              label={en.postal_code_label}
              placeholder={en.postal_code_label + '*'}
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              required={true}
              showError={errors.postalCode}
              errorMessage={en.postal_code_error}
              maxLength={20}
            />
            <div className="last-name-wrapper">
              <CustomTextbox
                label={en.last_name_label}
                placeholder={en.last_name_label + '*'}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required={true}
                showError={errors.lastName}
                errorMessage={en.last_name_error}
                maxLength={25}
              />
              <p className='last-name-info'>{en.last_name_info}</p>
            </div>
            <div className="form-button-container">
              <CustomButton type="submit" disabled={isSubmitting}>
                {en.common_continue}
              </CustomButton>
            </div>
          </form>
        </div>

        {/* Error Modal */}
        <CustomModal
          show={showErrorModal}
          onHide={() => setShowErrorModal(false)}
          size="md"
          scrollable
          dialogClassName="modal-error-info"
        >
          <div>
            <p className="error-modal-header">Error</p>
            <p className="error-modal-message">
              {errorMessage === 'ALREADY_ESCHEATED_TO_STATE' ? (
                <>
                  You are unable to claim your property because funds have
                  already been escheated to the state. All claims to this
                  property must now be made with your state via their website,
                  email or by calling. You may also visit{' '}
                  <a
                    href="https://www.missingmoney.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    www.missingmoney.com
                  </a>{' '}
                  to locate your funds.
                </>
              ) : (
                errorMessage
              )}
            </p>
            <CustomButton
              onClick={() => setShowErrorModal(false)}
              variant="full-width"
            >
              Close
            </CustomButton>
          </div>
        </CustomModal>
      </div>
    </>
  );
};

export default PropertyInfoForm;
